import type {
  Channel,
  Collection,
  MediaLayout,
  CollectionSummary,
  Card,
  Me,
  ParseResult,
  PostLimits,
  PostMediaItem,
  PostShape,
  StatsResponse,
  StatsWindow,
} from '../types';
import { getInitData } from './telegram';

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const initData = getInitData();

  const headers: Record<string, string> = {
    // Подпись Telegram — единственный способ, которым сервер узнаёт, кто мы.
    authorization: `tma ${initData}`,
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  // content-type ставим ТОЛЬКО когда тело действительно есть: заголовок при
  // пустом теле — это ошибка разбора на стороне сервера, а не мелочь.
  if (init.body !== undefined && init.body !== null) {
    headers['content-type'] = 'application/json';
  }

  // Мобильная сеть рвёт соединения, а через VPN это происходит чаще: один
  // сорвавшийся запрос не должен превращаться в «нет связи с сервером».
  //
  // Повторяем ТОЛЬКО чтение. Повторить публикацию или загрузку файла нельзя:
  // запрос мог дойти до сервера и выполниться, а оборваться уже на ответе —
  // тогда повтор опубликовал бы пост второй раз.
  const method = (init.method ?? 'GET').toUpperCase();
  const canRetry = method === 'GET';

  let res: Response | undefined;
  let lastError: unknown;
  for (let attempt = 0; attempt < (canRetry ? 3 : 1); attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, attempt * 400));
    }
    try {
      res = await fetch(`${BASE}${path}`, { ...init, headers });
      break;
    } catch (err) {
      lastError = err;
    }
  }
  if (!res) {
    void lastError;
    throw new ApiError('NETWORK', 'Нет связи с сервером', 0);
  }

  const text = await res.text();
  const body = text ? safeJson(text) : {};

  if (!res.ok) {
    const code = typeof body.error === 'string' ? body.error : 'HTTP_ERROR';
    const message =
      typeof body.message === 'string' ? body.message : `Ошибка ${res.status}`;
    throw new ApiError(code, message, res.status);
  }
  return body as T;
}

/**
 * Загрузка файла. Через XMLHttpRequest, а не fetch: только он даёт прогресс
 * отправки, а для видео на мобильном интернете это не украшательство.
 */
function upload<T>(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.setRequestHeader('authorization', `tma ${getInitData()}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      const body = safeJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
        return;
      }
      reject(
        new ApiError(
          typeof body.error === 'string' ? body.error : 'HTTP_ERROR',
          typeof body.message === 'string' ? body.message : `Ошибка ${xhr.status}`,
          xhr.status,
        ),
      );
    };
    xhr.onerror = () => reject(new ApiError('NETWORK', 'Нет связи с сервером', 0));
    xhr.onabort = () => reject(new ApiError('ABORTED', 'Загрузка прервана', 0));

    xhr.send(form);
  });
}

function safeJson(text: string): Record<string, unknown> {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Абсолютный путь до прокси-картинки (учитывает VITE_API_BASE). */
export function mediaUrl(path: string | null): string | undefined {
  return path ? `${BASE}${path}` : undefined;
}

export const api = {
  me: () => request<Me>('/api/me'),

  addChannel: (channel: string) =>
    request<{ channels: Channel[] }>('/api/me/channels', {
      method: 'POST',
      body: JSON.stringify({ channel }),
    }).then((r) => r.channels),

  removeChannel: (id: number) =>
    request<{ channels: Channel[] }>(`/api/me/channels/${id}`, { method: 'DELETE' }).then(
      (r) => r.channels,
    ),

  setDefaultChannel: (id: number) =>
    request<{ channels: Channel[] }>(`/api/me/channels/${id}/default`, {
      method: 'POST',
    }).then((r) => r.channels),

  /** Загрузка картинки товара с устройства. Возвращает готовый imageUrl. */
  uploadCardImage: (file: File, onProgress?: (percent: number) => void) =>
    upload<{ imageUrl: string }>('/api/card-image', file, onProgress).then((r) => r.imageUrl),

  parse: (payload: { text?: string; urls?: string[] }) =>
    request<{ results: ParseResult[] }>('/api/parse', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listCollections: () =>
    request<{ collections: CollectionSummary[] }>('/api/collections'),

  getCollection: (id: string) =>
    request<{ collection: Collection; limits: PostLimits }>(`/api/collections/${id}`),

  postPreview: (payload: { caption: string; mediaCount: number; buttonText?: string | null }) =>
    request<{
      preview: {
        html: string;
        /** Ровно то, что уйдёт в сообщение: у альбома со ссылкой внутри. */
        messageHtml: string;
        length: number;
        shape: PostShape;
        hasButton: boolean;
        buttonText: string;
        defaultButtonText: string;
        maxLength: number;
        mediaCaptionLimit: number;
      };
    }>('/api/post-preview', { method: 'POST', body: JSON.stringify(payload) }),

  uploadMedia: (collectionId: string, file: File, onProgress?: (percent: number) => void) =>
    upload<{ media: PostMediaItem }>(
      `/api/collections/${collectionId}/media`,
      file,
      onProgress,
    ).then((r) => r.media),

  deleteMedia: (collectionId: string, mediaId: string) =>
    request<{ media: PostMediaItem[] }>(`/api/collections/${collectionId}/media/${mediaId}`, {
      method: 'DELETE',
    }).then((r) => r.media),

  reorderMedia: (collectionId: string, ids: string[]) =>
    request<{ media: PostMediaItem[] }>(`/api/collections/${collectionId}/media/order`, {
      method: 'PATCH',
      body: JSON.stringify({ ids }),
    }).then((r) => r.media),

  createCollection: (payload: {
    caption: string;
    buttonText?: string | null;
    mediaLayout?: MediaLayout;
    cards: CardPayload[];
  }) =>
    request<{ collection: Collection }>('/api/collections', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateCollection: (
    id: string,
    payload: {
      caption?: string;
      buttonText?: string | null;
      mediaLayout?: MediaLayout;
      cards?: CardPayload[];
    },
  ) =>
    request<{ collection: Collection }>(`/api/collections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteCollection: (id: string) =>
    request<{ ok: true }>(`/api/collections/${id}`, { method: 'DELETE' }),

  publish: (id: string, channelId?: number) =>
    request<{ published: { chatId: string; messageId: number; deepLink: string } }>(
      `/api/collections/${id}/publish`,
      channelId === undefined
        ? { method: 'POST' }
        : { method: 'POST', body: JSON.stringify({ channelId }) },
    ),

  collectionStats: (id: string, window: StatsWindow) =>
    request<StatsResponse>(`/api/stats/collections/${id}?window=${window}`),

  click: (collectionId: string, cardId: string) =>
    request<{ url: string }>(`/api/collections/${collectionId}/cards/${cardId}/click`, {
      method: 'POST',
    }),
};

export type CardPayload = Pick<
  Card,
  'url' | 'title' | 'imageUrl' | 'brand' | 'price' | 'currency' | 'retailer'
> & {
  parsedBy?: string;
};
